/** Canvas export + remove-bg client for the dashboard free PFP tray. */

export const FREE_PFP_EXPORT_SIZE = 1024;
export const FREE_PFP_TOOL_ID = "free-pfp";

const VALID_TYPES = new Set(["image/png", "image/jpeg", "image/jpg", "image/webp"]);

export function isValidPfpUpload(file: File): boolean {
  return VALID_TYPES.has(file.type.toLowerCase());
}

export async function removeBackgroundFromFile(file: File): Promise<string> {
  const formData = new FormData();
  formData.append("file", file);

  const response = await fetch("/api/tools/remove-background", {
    method: "POST",
    body: formData,
  });

  return readCutoutResponse(response);
}

export async function pollLandingCutout(ppUid: string): Promise<string> {
  const response = await fetch(`/api/free-pfp/poll?pp_uid=${encodeURIComponent(ppUid)}`, {
    method: "GET",
    cache: "no-store",
  });
  return readCutoutResponse(response);
}

async function readCutoutResponse(response: Response): Promise<string> {
  const contentType = (response.headers.get("content-type") || "").toLowerCase();
  const isJson = contentType.includes("application/json");

  if (!response.ok || isJson) {
    let message = "Background removal failed";
    try {
      const data = (await response.json()) as { error?: string };
      if (data?.error) message = data.error;
    } catch {
      /* body wasn't JSON */
    }
    throw new Error(message);
  }

  const blob = await response.blob();
  if (!blob.size) throw new Error("Background removal failed");
  return blobToDataUrl(blob);
}

export async function composeFreePfp(opts: {
  cutoutUrl: string;
  fill?: string;
  backgroundUrl?: string;
  offsetX: number;
  offsetY: number;
  scale: number;
  rotate: number;
  flipH: boolean;
  flipV: boolean;
}): Promise<string> {
  const size = FREE_PFP_EXPORT_SIZE;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not create canvas");

  const [cutout, background] = await Promise.all([
    loadImage(opts.cutoutUrl),
    opts.backgroundUrl ? loadImage(opts.backgroundUrl) : Promise.resolve(null),
  ]);

  ctx.clearRect(0, 0, size, size);

  if (background) {
    drawCover(ctx, background, size);
  } else if (opts.fill) {
    ctx.fillStyle = cssFillToCanvas(ctx, opts.fill, size);
    ctx.fillRect(0, 0, size, size);
  } else {
    ctx.fillStyle = "#e5e7eb";
    ctx.fillRect(0, 0, size, size);
  }

  const { dw, dh, dx, dy } = containRect(cutout.width, cutout.height, size);
  ctx.save();
  ctx.translate(size / 2, size / 2);
  ctx.translate((opts.offsetX / 100) * size, (opts.offsetY / 100) * size);
  ctx.scale(opts.scale, opts.scale);
  ctx.rotate((opts.rotate * Math.PI) / 180);
  ctx.scale(opts.flipH ? -1 : 1, opts.flipV ? -1 : 1);
  ctx.translate(-size / 2, -size / 2);
  ctx.drawImage(cutout, dx, dy, dw, dh);
  ctx.restore();

  return canvas.toDataURL("image/png");
}

export async function downloadFreePfp(imageData: string, filename: string): Promise<void> {
  const response = await fetch("/api/download/image", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({
      imageData,
      filename,
      toolId: FREE_PFP_TOOL_ID,
      deductCredit: false,
    }),
  });

  if (!response.ok) {
    throw new Error("Download failed");
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  if (link.parentNode === document.body) {
    document.body.removeChild(link);
  }
  setTimeout(() => URL.revokeObjectURL(url), 100);
}

function isEditorEmbedded(src: string): boolean {
  try {
    return new URL(src).hostname === "editor-embedded.pfpmaker.com";
  } catch {
    return false;
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  const url =
    src.startsWith("http://") || src.startsWith("https://")
      ? isEditorEmbedded(src)
        ? `/api/free-pfp/asset?url=${encodeURIComponent(src)}`
        : src
      : src;

  return new Promise((resolve, reject) => {
    const img = new Image();
    if (!url.startsWith("data:") && !url.startsWith("blob:")) {
      img.crossOrigin = "anonymous";
    }
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to load image for export"));
    img.src = url;
  });
}

function containRect(imgW: number, imgH: number, size: number) {
  const aspect = imgW / imgH;
  let dw: number;
  let dh: number;
  if (aspect > 1) {
    dw = size;
    dh = size / aspect;
  } else {
    dh = size;
    dw = size * aspect;
  }
  return { dw, dh, dx: (size - dw) / 2, dy: (size - dh) / 2 };
}

function drawCover(ctx: CanvasRenderingContext2D, img: HTMLImageElement, size: number) {
  const aspect = img.width / img.height;
  let bw: number;
  let bh: number;
  if (aspect > 1) {
    bh = size;
    bw = size * aspect;
  } else {
    bw = size;
    bh = size / aspect;
  }
  ctx.drawImage(img, (size - bw) / 2, (size - bh) / 2, bw, bh);
}

function cssFillToCanvas(
  ctx: CanvasRenderingContext2D,
  fill: string,
  size: number
): CanvasGradient | string {
  if (!fill.includes("gradient")) return fill;

  const parsed = fill.match(/linear-gradient\(\s*(.+?)\s*,\s*(.+)\s*\)/i);
  if (!parsed) return fill;

  const dir = parsed[1].trim();
  const stopSrc = parsed[2];
  let x0: number;
  let y0: number;
  let x1: number;
  let y1: number;

  const degMatch = dir.match(/^([\d.]+)deg$/i);
  if (/^to\s+right$/i.test(dir)) {
    x0 = 0;
    y0 = size / 2;
    x1 = size;
    y1 = size / 2;
  } else if (/^to\s+left$/i.test(dir)) {
    x0 = size;
    y0 = size / 2;
    x1 = 0;
    y1 = size / 2;
  } else if (/^to\s+bottom$/i.test(dir)) {
    x0 = size / 2;
    y0 = 0;
    x1 = size / 2;
    y1 = size;
  } else if (/^to\s+top$/i.test(dir)) {
    x0 = size / 2;
    y0 = size;
    x1 = size / 2;
    y1 = 0;
  } else if (degMatch) {
    const rad = (parseFloat(degMatch[1]) * Math.PI) / 180;
    x0 = size / 2 - (Math.sin(rad) * size) / 2;
    y0 = size / 2 + (Math.cos(rad) * size) / 2;
    x1 = size / 2 + (Math.sin(rad) * size) / 2;
    y1 = size / 2 - (Math.cos(rad) * size) / 2;
  } else {
    return fill;
  }

  const gradient = ctx.createLinearGradient(x0, y0, x1, y1);
  const stopChunks = stopSrc.split(/,(?![^(]*\))/);
  stopChunks.forEach((chunk, index) => {
    const trimmed = chunk.trim();
    const stopMatch = trimmed.match(/^(.+?)\s+([\d.]+)%$/);
    const color = stopMatch ? stopMatch[1].trim() : trimmed;
    const position = stopMatch ? parseFloat(stopMatch[2]) / 100 : index / Math.max(1, stopChunks.length - 1);
    try {
      gradient.addColorStop(Math.min(1, Math.max(0, position)), color);
    } catch {
      /* invalid stop — skip */
    }
  });

  return gradient;
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error("Failed to read processed image"));
    reader.readAsDataURL(blob);
  });
}
